import React, { useState, useEffect, useMemo } from 'react';
import { Card, Row, Col, Statistic, Table, Select, Spin, Typography, Tag, Empty, Space, Progress, Tooltip } from 'antd';
import {
  RobotOutlined, MessageOutlined, ThunderboltOutlined, CheckCircleFilled,
  DollarOutlined, FireOutlined, ClockCircleOutlined, ToolOutlined,
  RiseOutlined, UserOutlined
} from '@ant-design/icons';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, PointElement, LineElement,
  BarElement, ArcElement, Title as ChartTitle, Tooltip as ChartTooltip,
  Legend, Filler
} from 'chart.js';
import {
  getAIStats, getAIToolStats, getUserActivity, getUsageTimeline,
  getAITotals, getCostAnalysis, getAgentUsage
} from '../services/adminService';

const { Title, Text } = Typography;

ChartJS.register(
  CategoryScale, LinearScale, PointElement, LineElement, BarElement,
  ArcElement, ChartTitle, ChartTooltip, Legend, Filler
);

const MODEL_COLORS = { 'claude-haiku-4-5': '#52c41a', 'claude-sonnet-4-6': '#1890ff', 'claude-opus-4-6': '#722ed1', unknown: '#bfbfbf' };
const MODEL_LABELS = { 'claude-haiku-4-5': 'Haiku 4.5', 'claude-sonnet-4-6': 'Sonnet 4.6', 'claude-opus-4-6': 'Opus 4.6', unknown: 'Legacy' };
const MODEL_TIERS = {
  'claude-haiku-4-5': { tag: '1x cost', tagColor: 'green', input: '$1', output: '$5' },
  'claude-sonnet-4-6': { tag: '3x cost', tagColor: 'blue', input: '$3', output: '$15' },
  'claude-opus-4-6':  { tag: '5x cost', tagColor: 'purple', input: '$15', output: '$75' },
};

const kpiStyle = (accent) => ({ borderLeft: `4px solid ${accent}`, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' });

function AIUsage() {
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [toolStats, setToolStats] = useState([]);
  const [userActivity, setUserActivity] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [aiTotals, setAiTotals] = useState(null);
  const [costAnalysis, setCostAnalysis] = useState([]);
  const [agentUsage, setAgentUsage] = useState([]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [s, t, u, tl, totals, costs, agents] = await Promise.all([
        getAIStats(days),
        getAIToolStats(days),
        getUserActivity(days),
        getUsageTimeline(days),
        getAITotals(days),
        getCostAnalysis(days),
        getAgentUsage(days),
      ]);
      setStats(s);
      setToolStats(t.tools || []);
      setUserActivity((u.users || []).sort((a, b) => (b.interactions || 0) - (a.interactions || 0)));
      setTimeline(tl.timeline || []);
      setAiTotals(totals);
      setCostAnalysis(costs);
      setAgentUsage(agents);
    } catch (err) {
      console.error('Failed to load AI usage data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, [days]); // eslint-disable-line

  const modelBreakdown = aiTotals?.model_breakdown || [];

  // ── Timeline stacked bar chart ──
  const allModelKeys = useMemo(() => {
    const keys = new Set();
    timeline.forEach(d => { if (d.per_model_interactions) Object.keys(d.per_model_interactions).forEach(k => keys.add(k)); });
    costAnalysis.forEach(d => { if (d.per_model) Object.keys(d.per_model).forEach(k => keys.add(k)); });
    modelBreakdown.forEach(m => keys.add(m.model_key));
    return Array.from(keys).filter(Boolean);
  }, [timeline, costAnalysis, modelBreakdown]);

  const timelineChartData = useMemo(() => ({
    labels: timeline.map(d => d.date),
    datasets: [
      ...allModelKeys.map(mk => ({
        type: 'bar', label: MODEL_LABELS[mk] || mk,
        data: timeline.map(d => (d.per_model_interactions || {})[mk] || 0),
        backgroundColor: (MODEL_COLORS[mk] || '#aaa') + 'cc',
        borderColor: MODEL_COLORS[mk] || '#aaa', borderWidth: 1, stack: 'interactions', borderRadius: 3,
      })),
      {
        type: 'line', label: 'Total',
        data: timeline.map(d => d.interactions),
        borderColor: '#ff7a00', backgroundColor: 'transparent', borderWidth: 2, pointRadius: 1.5, tension: 0.4,
      },
    ]
  }), [timeline, allModelKeys]);

  // ── Cost trend chart ──
  const costChartData = useMemo(() => ({
    labels: costAnalysis.map(c => c.date),
    datasets: [
      ...allModelKeys.map(mk => ({
        type: 'bar', label: MODEL_LABELS[mk] || mk,
        data: costAnalysis.map(c => parseFloat(((c.per_model || {})[mk]?.cost || 0).toFixed(4))),
        backgroundColor: (MODEL_COLORS[mk] || '#aaa') + 'cc',
        borderColor: MODEL_COLORS[mk] || '#aaa', borderWidth: 1, stack: 'cost', borderRadius: 3,
      })),
      {
        type: 'line', label: 'Total Cost',
        data: costAnalysis.map(c => parseFloat(c.cost)),
        borderColor: '#f5222d', backgroundColor: 'transparent', borderWidth: 2, pointRadius: 1.5, tension: 0.4,
      }
    ]
  }), [costAnalysis, allModelKeys]);

  // ── Token area chart ──
  const tokenAreaData = useMemo(() => ({
    labels: timeline.map(d => d.date),
    datasets: allModelKeys.map((mk, i) => ({
      label: MODEL_LABELS[mk] || mk,
      data: timeline.map(d => (d.per_model_tokens || {})[mk] || 0),
      backgroundColor: (MODEL_COLORS[mk] || '#aaa') + '40',
      borderColor: MODEL_COLORS[mk] || '#aaa', borderWidth: 2, fill: i === 0 ? 'origin' : '-1', tension: 0.4, pointRadius: 1.5,
    })),
  }), [timeline, allModelKeys]);

  // ── Usage by page doughnut ──
  const agentChartData = useMemo(() => ({
    labels: agentUsage.map(a => a.context_type || 'General'),
    datasets: [{
      data: agentUsage.map(a => a.count),
      backgroundColor: ['#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1', '#13c2c2', '#fa8c16'],
      borderWidth: 2, borderColor: 'white',
    }],
  }), [agentUsage]);

  // ── Tool bar chart ──
  const topToolsData = useMemo(() => {
    const palette = ['#1890ff', '#52c41a', '#722ed1', '#faad14', '#13c2c2', '#f5222d', '#fa8c16', '#2f54eb'];
    const top = toolStats.slice(0, 8);
    return {
      labels: top.map(t => t.tool_name),
      datasets: [{ label: 'Usage', data: top.map(t => t.usage_count), backgroundColor: top.map((_, i) => palette[i % palette.length] + 'cc'), borderRadius: 6, barThickness: 22 }],
    };
  }, [toolStats]);

  const barOpts = (yFmt) => ({
    responsive: true, maintainAspectRatio: false, interaction: { mode: 'index', intersect: false },
    plugins: { legend: { display: true, position: 'top', labels: { usePointStyle: true, pointStyle: 'rectRounded', padding: 16, font: { size: 11 } } } },
    scales: {
      x: { stacked: true, ticks: { font: { size: 11 } } },
      y: { stacked: false, beginAtZero: true, ticks: { font: { size: 11 }, ...(yFmt === 'dollar' ? { callback: v => '$' + v.toFixed(2) } : {}) } },
    },
  });

  const areaOpts = {
    responsive: true, maintainAspectRatio: false, interaction: { mode: 'index', intersect: false },
    plugins: { legend: { display: true, position: 'top', labels: { usePointStyle: true, pointStyle: 'rectRounded', padding: 16, font: { size: 11 } } },
      tooltip: { callbacks: { label: ctx => `${ctx.dataset.label}: ${ctx.parsed.y?.toLocaleString() || 0} tokens` } } },
    scales: { x: { stacked: true }, y: { stacked: true, beginAtZero: true } },
  };

  const doughnutOpts = {
    responsive: true, maintainAspectRatio: false, cutout: '55%',
    plugins: {
      legend: { position: 'right', labels: { usePointStyle: true, pointStyle: 'circle', padding: 12, font: { size: 11 } } },
      tooltip: { callbacks: { label: ctx => { const total = ctx.dataset.data.reduce((a, b) => a + b, 0); return `${ctx.label}: ${ctx.parsed} (${((ctx.parsed / total) * 100).toFixed(1)}%)`; } } },
    },
  };

  const hbarOpts = {
    responsive: true, maintainAspectRatio: false, indexAxis: 'y',
    plugins: { legend: { display: false } },
    scales: { x: { ticks: { font: { size: 11 } } }, y: { ticks: { font: { size: 11 } }, grid: { display: false } } },
  };

  const totalInputTokens = (aiTotals?.input_tokens || 0).toLocaleString();
  const totalOutputTokens = (aiTotals?.output_tokens || 0).toLocaleString();

  // ── User table columns ──
  const userColumns = [
    { title: '#', key: 'rank', width: 45, render: (_, __, i) => <Text style={{ fontWeight: 600 }}>{i + 1}</Text> },
    {
      title: 'User', key: 'user',
      render: (_, r) => (<div><Text style={{ fontWeight: 500 }}>{r.username}</Text><br /><Text type="secondary" style={{ fontSize: 11 }}>{r.email}</Text></div>),
      sorter: (a, b) => a.username.localeCompare(b.username),
    },
    { title: 'Role', dataIndex: 'role', key: 'role', render: r => <Tag>{r}</Tag>, filters: [{ text: 'Admin', value: 'Admin' }, { text: 'Creator', value: 'Creator' }, { text: 'User', value: 'User' }], onFilter: (v, rec) => rec.role === v },
    { title: 'Interactions', dataIndex: 'interactions', key: 'interactions', sorter: (a, b) => a.interactions - b.interactions, defaultSortOrder: 'descend', render: v => <Text strong style={{ color: '#1890ff' }}>{(v || 0).toLocaleString()}</Text> },
    { title: 'Total Tokens', dataIndex: 'total_tokens', key: 'total_tokens', sorter: (a, b) => (a.total_tokens || 0) - (b.total_tokens || 0), render: v => (v || 0).toLocaleString() },
    { title: 'Last Active', dataIndex: 'last_active', key: 'last_active', sorter: (a, b) => new Date(a.last_active || 0) - new Date(b.last_active || 0), render: v => v ? new Date(v).toLocaleDateString() : '—' },
  ];

  // ── Tool table columns ──
  const toolColumns = [
    { title: 'Tool', dataIndex: 'tool_name', key: 'tool_name', sorter: (a, b) => a.tool_name.localeCompare(b.tool_name) },
    { title: 'Count', dataIndex: 'usage_count', key: 'usage_count', width: 80, sorter: (a, b) => a.usage_count - b.usage_count, defaultSortOrder: 'descend' },
    { title: 'Avg Time', dataIndex: 'avg_time_ms', key: 'avg_time_ms', width: 90, render: v => { if (!v) return '—'; return v < 1000 ? <Tag color="green">{v}ms</Tag> : <Tag color={v < 3000 ? 'gold' : 'red'}>{(v / 1000).toFixed(1)}s</Tag>; } },
  ];

  return (
    <div style={{ padding: 24, minHeight: '100vh' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <Title level={3} style={{ margin: 0, fontWeight: 'bold', textTransform: 'uppercase' }}>
          <RobotOutlined /> AI USAGE ANALYTICS
        </Title>
        <Space>
          <ClockCircleOutlined />
          <Text strong>Time Range:</Text>
          <Select value={days} onChange={setDays} style={{ width: 160 }}
            options={[{ value: 7, label: 'Last 7 days' }, { value: 30, label: 'Last 30 days' }, { value: 90, label: 'Last 90 days' }, { value: 365, label: 'Last year' }]} />
        </Space>
      </div>

      {/* 4 KPI Cards */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card size="small" style={kpiStyle('#1890ff')}>
            <Statistic title="Total Interactions" value={stats?.total_interactions || 0} prefix={<MessageOutlined style={{ color: '#1890ff' }} />} valueStyle={{ color: '#1890ff', fontWeight: 'bold' }} />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card size="small" style={kpiStyle('#722ed1')}>
            <Tooltip title={`Input: ${totalInputTokens} · Output: ${totalOutputTokens} · Avg: ${(aiTotals?.avg_tokens_per_interaction || 0).toLocaleString()}/interaction`}>
              <Statistic title="Total Tokens" value={aiTotals?.total_tokens || 0} prefix={<FireOutlined style={{ color: '#722ed1' }} />} valueStyle={{ color: '#722ed1', fontWeight: 'bold' }} />
            </Tooltip>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card size="small" style={kpiStyle('#f5222d')}>
            <Tooltip title="Pricing: Haiku $1/$5 · Sonnet $3/$15 · Opus $15/$75 per MTok (In/Out)">
              <Statistic title="Total Cost (Est.)" value={aiTotals?.total_cost || 0} prefix={<DollarOutlined style={{ color: '#f5222d' }} />} precision={0} valueStyle={{ color: '#f5222d', fontWeight: 'bold' }} />
            </Tooltip>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card size="small" style={kpiStyle('#52c41a')}>
            <Statistic title="Success Rate" value={stats?.success_rate || 0} suffix="%" prefix={<CheckCircleFilled style={{ color: '#52c41a' }} />} valueStyle={{ color: '#52c41a', fontWeight: 'bold' }} />
          </Card>
        </Col>
      </Row>

      <Spin spinning={loading}>
        {/* Activity Timeline + Cost Trend */}
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} lg={12}>
            <Card title={<Space><RiseOutlined style={{ color: '#1890ff' }} /><span style={{ fontWeight: 600 }}>AI Activity Timeline</span></Space>}>
              <div style={{ height: 280 }}>
                {timeline.length > 0 ? <Bar data={timelineChartData} options={barOpts()} /> : <Empty description="No timeline data" />}
              </div>
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card title={<Space><DollarOutlined style={{ color: '#f5222d' }} /><span style={{ fontWeight: 600 }}>Cost Trend</span></Space>}>
              <div style={{ height: 280 }}>
                {costAnalysis.length > 0 ? <Bar data={costChartData} options={barOpts('dollar')} /> : <Empty description="No cost data" />}
              </div>
            </Card>
          </Col>
        </Row>

        {/* Token Consumption Over Time */}
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24}>
            <Card title={<Space><ThunderboltOutlined style={{ color: '#722ed1' }} /><span style={{ fontWeight: 600 }}>Token Consumption Over Time</span></Space>}>
              <div style={{ height: 260 }}>
                {timeline.length > 0 ? <Line data={tokenAreaData} options={areaOpts} /> : <Empty description="No token data" />}
              </div>
            </Card>
          </Col>
        </Row>

        {/* Model Usage Breakdown */}
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24}>
            <Card title={<Space><FireOutlined style={{ color: '#722ed1' }} /><span style={{ fontWeight: 600 }}>Model Usage Breakdown</span></Space>}>
              <Row gutter={[16, 16]}>
                {(modelBreakdown.length > 0 ? modelBreakdown : [
                  { model_key: 'claude-haiku-4-5', interactions: 0, tokens: 0, input_tokens: 0, output_tokens: 0, cost: 0 },
                  { model_key: 'claude-sonnet-4-6', interactions: 0, tokens: 0, input_tokens: 0, output_tokens: 0, cost: 0 },
                  { model_key: 'claude-opus-4-6', interactions: 0, tokens: 0, input_tokens: 0, output_tokens: 0, cost: 0 },
                ]).map(model => {
                  const tier = MODEL_TIERS[model.model_key] || { tag: '?', tagColor: 'default', input: '?', output: '?' };
                  const totalInt = modelBreakdown.reduce((s, m) => s + (m.interactions || 0), 0);
                  const pct = totalInt > 0 ? (model.interactions || 0) / totalInt * 100 : 0;
                  const accent = MODEL_COLORS[model.model_key] || '#aaa';
                  return (
                    <Col xs={24} sm={8} key={model.model_key}>
                      <Card size="small" style={{ borderLeft: `4px solid ${accent}`, borderRadius: 8 }}>
                        <Row justify="space-between" align="middle" style={{ marginBottom: 8 }}>
                          <Text strong style={{ fontSize: 15 }}>{MODEL_LABELS[model.model_key] || model.model_key}</Text>
                          <Tag color={tier.tagColor}>{tier.tag}</Tag>
                        </Row>
                        <Progress percent={pct} strokeColor={accent} showInfo={false} size="small" style={{ marginBottom: 10 }} />
                        <Row gutter={8}>
                          <Col span={8}><Statistic title={<Text style={{ fontSize: 10 }}>Calls</Text>} value={model.interactions || 0} valueStyle={{ fontSize: 16, fontWeight: 600 }} /></Col>
                          <Col span={8}><Statistic title={<Text style={{ fontSize: 10 }}>Tokens</Text>} value={model.tokens || 0} valueStyle={{ fontSize: 16, fontWeight: 600 }} formatter={v => v >= 1e6 ? `${(v / 1e6).toFixed(1)}M` : v >= 1000 ? `${(v / 1000).toFixed(0)}K` : v} /></Col>
                          <Col span={8}><Statistic title={<Text style={{ fontSize: 10 }}>Cost</Text>} value={model.cost || 0} prefix="$" precision={2} valueStyle={{ fontSize: 16, fontWeight: 600, color: (model.cost || 0) < 1 ? '#52c41a' : (model.cost || 0) < 10 ? '#faad14' : '#f5222d' }} /></Col>
                        </Row>
                        <div style={{ marginTop: 8, borderTop: '1px solid #f0f0f0', paddingTop: 6 }}>
                          <Text style={{ fontSize: 10, color: '#999' }}>Pricing: In {tier.input} · Out {tier.output} / 1M tokens</Text>
                        </div>
                      </Card>
                    </Col>
                  );
                })}
              </Row>
            </Card>
          </Col>
        </Row>

        {/* Tool Usage + Usage by Page */}
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} lg={14}>
            <Card title={<Space><ToolOutlined style={{ color: '#1890ff' }} /><span style={{ fontWeight: 600 }}>AI Tool Usage</span></Space>}>
              <div style={{ height: 240, marginBottom: 16 }}>
                {toolStats.length > 0 ? <Bar data={topToolsData} options={hbarOpts} /> : <Empty description="No tool data" />}
              </div>
              <Table columns={toolColumns} dataSource={toolStats} rowKey="tool_name" size="small" pagination={{ pageSize: 5, size: 'small' }} />
            </Card>
          </Col>
          <Col xs={24} lg={10}>
            <Card title={<Space><RobotOutlined style={{ color: '#722ed1' }} /><span style={{ fontWeight: 600 }}>Usage by Page</span></Space>}>
              <div style={{ height: 240, marginBottom: 16 }}>
                {agentUsage.length > 0 ? <Doughnut data={agentChartData} options={doughnutOpts} /> : <Empty description="No page data" />}
              </div>
              <Table
                dataSource={agentUsage} rowKey="context_type" size="small" pagination={false}
                columns={[
                  { title: 'Location', dataIndex: 'context_type', key: 'context_type', render: t => <Text style={{ fontWeight: 500 }}>{t || 'General'}</Text> },
                  { title: 'Calls', dataIndex: 'count', key: 'count', width: 70, sorter: (a, b) => a.count - b.count, render: v => <Text strong>{v?.toLocaleString()}</Text> },
                  { title: 'Cost', dataIndex: 'total_cost', key: 'total_cost', width: 80, render: v => <Tag color="gold">${(v || 0).toFixed(2)}</Tag> },
                ]}
              />
            </Card>
          </Col>
        </Row>

        {/* User Table */}
        <Card title={<Space><UserOutlined style={{ color: '#1890ff' }} /><span style={{ fontWeight: 600 }}>Users by AI Usage</span></Space>} extra={<Tag color="blue">{userActivity.length} users</Tag>}>
          <Table columns={userColumns} dataSource={userActivity} rowKey="user_id" size="small" pagination={{ pageSize: 10, showSizeChanger: true, showTotal: total => `${total} users` }} />
        </Card>
      </Spin>
    </div>
  );
}

export default AIUsage;
